import type { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { Skeleton as LibSkeleton } from '@elfsight/widget-ui';
import {
  createReadableClass,
  useStableBreakpoints
} from '@elfsight/widget-utils';
import { CLASS_COMPONENT } from '../../common';
import { Section } from './section';

const LOADER_BREAKPOINTS = {
  mobile: 600,
  tablet: 768
};

const DETAILS_HEIGHT = 78;
const DETAILS_WITHOUT_NAME_HEIGHT = 50;
const TOTAL_HEIGHT = 22;
const PAYMENT_TITLE_HEIGHT = 22;
const PAY_BUTTON_WITH_DESCRIPTION_HEIGHT = 72;

const TAB_SKELETON_HEIGHT = 64.8;
const FIELD_SKELETON_HEIGHT = 50;

export function CheckoutLoadingContainer({
  textColor,
  isLoading,
  withDataSections = true,
  detailsWithName = true,
  withCondensedFields = false,
  tabsGap = 20,
  children
}: PropsWithChildren<{
  textColor: string;
  withDataSections?: boolean;
  detailsWithName?: boolean;
  isLoading: boolean;
  withCondensedFields?: boolean;
  tabsGap?: number;
}>) {
  return (
    <LoaderContainer>
      <Show when={isLoading}>
        <Show when={withDataSections}>
          <Section $withBottomDivider $withoutTopPadding $color={textColor}>
            <Skeleton
              height={
                detailsWithName ? DETAILS_HEIGHT : DETAILS_WITHOUT_NAME_HEIGHT
              }
            />
          </Section>

          <Section $withBottomDivider $color={textColor}>
            <Skeleton height={TOTAL_HEIGHT} />
          </Section>

          <Section $color={textColor}>
            <Skeleton height={PAYMENT_TITLE_HEIGHT} />
          </Section>
        </Show>

        <StripeFormLoader
          tabsGap={tabsGap}
          withCondensedFields={withCondensedFields}
        />

        <Section>
          <Skeleton height={PAY_BUTTON_WITH_DESCRIPTION_HEIGHT} />
        </Section>
      </Show>
      <ContentContainer $isLoading={isLoading}>{children}</ContentContainer>
    </LoaderContainer>
  );
}

type TabSkeletonName = 'tab-one' | 'tab-two';
type FieldSkeletonName = 'card-num' | 'exp-date' | 'sec-code' | 'region';
type LoaderSkeletonName = TabSkeletonName | FieldSkeletonName;

export function StripeFormLoadingContainer({
  isLoading,
  children,
  withCondensedFields = false,
  tabsGap = 20
}: PropsWithChildren<{
  isLoading: boolean;
  withCondensedFields?: boolean;
  tabsGap?: number;
}>) {
  return (
    <LoaderContainer>
      <Show when={isLoading}>
        <StripeFormLoader
          withCondensedFields={withCondensedFields}
          tabsGap={tabsGap}
        />
      </Show>
      <ContentContainer $isLoading={isLoading}>{children}</ContentContainer>
    </LoaderContainer>
  );
}

function StripeFormLoader({
  tabsGap = 20,
  withCondensedFields = false
}: {
  tabsGap?: number;
  withCondensedFields?: boolean;
}) {
  const [measureRef, breakpoint] = useStableBreakpoints(
    LOADER_BREAKPOINTS,
    'desktop'
  );

  const inputsLayout = withCondensedFields ? 'condensed' : 'spaced';
  const fieldsBorderRadius = withCondensedFields ? 0 : 8;

  return (
    <StripeFormLoaderLayout data-inputs-layout={inputsLayout} ref={measureRef}>
      <TabsLayout $gap={tabsGap}>
        <Skeleton height={TAB_SKELETON_HEIGHT} area="tab-one" />
        <Skeleton height={TAB_SKELETON_HEIGHT} area="tab-two" />
      </TabsLayout>

      <FieldsLayout
        data-breakpoint={breakpoint}
        data-inputs-layout={inputsLayout}
      >
        <Skeleton
          height={FIELD_SKELETON_HEIGHT}
          area="card-num"
          borderRadius={fieldsBorderRadius}
        />
        <Skeleton
          height={FIELD_SKELETON_HEIGHT}
          area="exp-date"
          borderRadius={fieldsBorderRadius}
        />
        <Skeleton
          height={FIELD_SKELETON_HEIGHT}
          area="sec-code"
          borderRadius={fieldsBorderRadius}
        />
        <Skeleton
          height={FIELD_SKELETON_HEIGHT}
          area="region"
          borderRadius={fieldsBorderRadius}
        />
      </FieldsLayout>
    </StripeFormLoaderLayout>
  );
}

const StripeFormLoaderLayout = styled.div.attrs(() => ({
  className: createReadableClass({
    component: CLASS_COMPONENT,
    element: 'payment-form-loader-layout'
  })
}))`
  width: 100%;
  display: grid;
  gap: 20px;
`;

const TabsLayout = styled.div<{ $gap: number }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: ${({ $gap }) => $gap}px;
`;

const FieldsLayout = styled.div`
  width: 100%;
  display: grid;

  &[data-inputs-layout='spaced'] {
    gap: 20px;
  }

  &[data-inputs-layout='condensed'] {
    gap: 0px;
    border-radius: 8px;
    overflow: hidden;
  }

  &[data-breakpoint='mobile'] {
    grid-template-areas:
      'card-num card-num'
      'exp-date exp-date'
      'sec-code sec-code'
      'region region';
  }

  &[data-breakpoint='tablet'] {
    grid-template-areas:
      'card-num card-num'
      'exp-date sec-code'
      'region region';
  }

  &[data-breakpoint='desktop'] {
    grid-template-areas:
      'card-num card-num exp-date sec-code'
      'region region region region';
  }
`;

function Skeleton({
  height,
  area,
  borderRadius = 8
}: {
  height: number | string;
  borderRadius?: number;
  area?: LoaderSkeletonName;
}) {
  return (
    <AreaSkeleton
      width="100%"
      borderRadius={borderRadius}
      height={height}
      $area={area}
    />
  );
}

const AreaSkeleton = styled(LibSkeleton)<{ $area?: LoaderSkeletonName }>`
  ${({ $area }) => $area && `grid-area: ${$area}`};
`;

const LoaderContainer = styled.div`
  position: relative;
  width: 100%;
  transition: height 0.5s ease-out;
`;

const ContentContainer = styled.div<{
  $isLoading: boolean;
}>`
  ${({ $isLoading }) =>
    $isLoading &&
    css`
      position: absolute;
      opacity: 0;
    `}
`;
